import { Timestamp } from "@bufbuild/protobuf";

import React, { useContext, useEffect, useRef, useState } from "react";

import { CheckCircle, Delete, Help, Inventory } from "@mui/icons-material";
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import clsx from "clsx";
import { DateTime } from "luxon";
import { Clipboard } from "react-feather";
import { Link } from "react-router-dom";
import mainStyles from "styles";

import { SortDirection } from "@safetyculture/s12-apis-connect-web/s12/common/sorting_pb";
import { Template } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_documents_pb";
import {
  DocumentQuery,
  DocumentSortType,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { DocumentSearch } from "./constants";
import { getOrganizationIdentity } from "api/endpoints/organisation";
import { listOrgTemplates } from "api/endpoints/templates-inspections";
import { callOptionsHeaders } from "api/helpers";
import DatePicker from "components/shared/date-picker";
import DisplayError from "components/shared/display-error";
import KebabMenu from "components/shared/kebab-menu";
import { DEFAULT_REGION_CODE, DocumentState } from "constants/index";
import { SnackbarContext } from "context/snackbar-context";
import { TemplateSearchQueryContext } from "context/template-search-context";
import { mapRegionStringToRegionCode } from "helpers/env-values-helper";
import {
  convertUUIDtoS12Id,
  validateS12ID,
  validateUUID,
} from "helpers/s12-helpers";
import { convertToTimestamp, formatDateTimeDisplay } from "helpers/time";

const DEFAULT_TEMPLATE_SEARCH_PAGE_SIZE = 25;
const TOTAL_ITEMS_UNINITIALISED = -1;

type TemplateSearchSortParam = {
  field: DocumentSortType;
  direction: SortDirection;
};

type TemplateSearchProps = {
  orgID?: string;
};

const TemplateSearch = ({ orgID }: TemplateSearchProps) => {
  const theme = useTheme();
  const classes = mainStyles();
  const { templateSearchQueryState, setTemplateSearchQueryState } = useContext(
    TemplateSearchQueryContext,
  );
  const { copyToClipboard, showError } = useContext(SnackbarContext);

  const [errorValidationMessage, setErrorValidationMessage] = useState("");
  const [regionCode, setRegionCode] = useState("unknown");
  const [templateSearchFormState, setTemplateSearchFormState] = useState(
    templateSearchQueryState,
  );
  const [sortParam, setSortParam] = useState<TemplateSearchSortParam>({
    field: DocumentSortType.NAME,
    direction: SortDirection.ASC,
  });
  const [templates, setTemplates] = useState<Template[]>([]);
  const [totalItems, setTotalItems] = useState(TOTAL_ITEMS_UNINITIALISED);
  const [pageSize, setPageSize] = useState(DEFAULT_TEMPLATE_SEARCH_PAGE_SIZE);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const isFirstRender = useRef(true);

  const minDate = DateTime.now().minus({ years: 5 }).startOf("day").toJSDate();
  const maxDate = DateTime.now().plus({ days: 1 }).endOf("day").toJSDate();
  const defaultDateFrom = DateTime.now()
    .minus({ years: 1 })
    .startOf("day")
    .toJSDate();

  const handleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent,
    key: string,
  ) => {
    let value;
    switch (key) {
      case "orgID":
      case "ownerID":
        value = event.target.value.trim();
        break;
      default:
        value = event.target.value;
    }
    setTemplateSearchFormState({
      ...templateSearchFormState,
      [key]: value,
    });
  };

  useEffect(() => {
    if (orgID) {
      const localTemplateSearchFormState = templateSearchFormState;
      localTemplateSearchFormState.orgID = orgID;
      setTemplateSearchFormState(localTemplateSearchFormState);
    }
  }, [orgID]);

  useEffect(() => {
    handleListOrgTemplates();
  }, [templateSearchFormState.orgID]);

  const handleDateChange = (date: Date, key: string) => {
    setTemplateSearchFormState({
      ...templateSearchFormState,
      [key]: date,
    });
  };

  const isValidInput = () => {
    if (!templateSearchFormState.orgID) {
      setErrorValidationMessage("Org ID cannot be empty");
      return false;
    }
    if (
      !validateUUID(templateSearchFormState.orgID) &&
      !validateS12ID(templateSearchFormState.orgID)
    ) {
      setErrorValidationMessage("Org ID is not a valid UUID or S12 ID");
      return false;
    }
    if (
      !!templateSearchFormState.ownerID &&
      !validateUUID(templateSearchFormState.ownerID) &&
      !validateS12ID(templateSearchFormState.ownerID)
    ) {
      setErrorValidationMessage("Owner ID is not a valid UUID or S12 ID");
      return false;
    }
    if (templateSearchFormState.dateFrom > templateSearchFormState.dateTo) {
      setErrorValidationMessage("From date cannot be later than to date");
      return false;
    }
    if (
      templateSearchFormState.dateFrom < minDate ||
      templateSearchFormState.dateFrom > maxDate ||
      templateSearchFormState.dateTo < minDate ||
      templateSearchFormState.dateTo > maxDate
    ) {
      setErrorValidationMessage("Date is outside the accepted ranges");
      return false;
    }
    if (templateSearchFormState.state === DocumentState.UNSPECIFIED) {
      setErrorValidationMessage("State is not valid");
      return false;
    }

    return true;
  };

  const handleListOrgTemplates = async () => {
    isFirstRender.current = false;
    if (!isValidInput()) {
      return;
    }

    setErrorValidationMessage("");
    setIsLoading(true);

    const orgS12ID = convertUUIDtoS12Id(
      templateSearchFormState.orgID,
      DocumentSearch.Role,
    );
    const orgResp = await getOrganizationIdentity(
      { orgId: orgS12ID },
      callOptionsHeaders(DEFAULT_REGION_CODE, orgS12ID),
    ).catch(showError);
    if (!orgResp?.region) {
      setRegionCode("unknown");
      setIsLoading(false);
      return;
    }
    const region = mapRegionStringToRegionCode(orgResp.region);
    setRegionCode(region);

    listOrgTemplates(
      {
        orgId: orgS12ID,
        pageSize: pageSize,
        offset: offset,
        query: new DocumentQuery({
          ownerId: templateSearchFormState.ownerID,
          text: templateSearchFormState.name,
          fromDate: convertToTimestamp(templateSearchFormState.dateFrom),
          toDate: convertToTimestamp(templateSearchFormState.dateTo),
          trashed:
            templateSearchFormState.state === DocumentState.ARCHIVED ||
            templateSearchFormState.state === DocumentState.DELETED,
        }),
        includeDeletedAndTrashed:
          templateSearchFormState.state === DocumentState.DELETED,
        sortType: sortParam.field,
        sortDirection: sortParam.direction,
      },
      callOptionsHeaders(region, orgS12ID),
    )
      .then((resp) => {
        setTemplates(resp.templates);
        setTotalItems(resp.totalItems);
        if (resp.totalItems === 0) {
          setOffset(0);
        }

        if (!orgID) {
          window.history.replaceState(
            null,
            "",
            `${location.pathname}?roleId=${templateSearchFormState.orgID}`,
          );
        }
      })
      .catch(showError)
      .finally(() => setIsLoading(false));
  };

  const clearForm = () => {
    setErrorValidationMessage("");
    setTemplateSearchQueryState({
      ...templateSearchQueryState,
      ownerID: "",
      name: "",
      dateFrom: defaultDateFrom,
      dateTo: maxDate,
      state: DocumentState.ACTIVE,
    });
    setTemplateSearchFormState({
      ...templateSearchFormState,
      ownerID: "",
      name: "",
      dateFrom: defaultDateFrom,
      dateTo: maxDate,
      state: DocumentState.ACTIVE,
    });

    isFirstRender.current = true;
    setTemplates([]);
    setTotalItems(TOTAL_ITEMS_UNINITIALISED);
    setOffset(0);
  };

  useEffect(() => {
    if (isFirstRender.current) {
      return;
    }

    handleListOrgTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, pageSize, sortParam]);

  useEffect(() => {
    if (!templateSearchQueryState.orgID && isFirstRender.current) {
      handleListOrgTemplates();

      return;
    }

    setTemplateSearchFormState(templateSearchQueryState);
    handleListOrgTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateSearchQueryState]);

  const displayDate = (timestamp: Timestamp | undefined) => {
    if (!timestamp || timestamp.toDate().getFullYear() < 2004) {
      return "N/A";
    }

    return formatDateTimeDisplay(timestamp.toDate());
  };

  const displayStateIcon = (
    archived: boolean | undefined,
    trashed: boolean,
  ) => {
    if (archived === undefined) {
      return (
        <Tooltip title="Unknown">
          <Help color="disabled" />
        </Tooltip>
      );
    }
    if (!archived && !trashed) {
      return (
        <Tooltip title="Active">
          <CheckCircle color="success" />
        </Tooltip>
      );
    }
    if (archived && !trashed) {
      return (
        <Tooltip title="Archived">
          <Inventory color="warning" />
        </Tooltip>
      );
    }
    return (
      <Tooltip title="Deleted">
        <Delete color="error" />
      </Tooltip>
    );
  };

  const displayTemplateRow = (template: Template) => {
    return (
      <TableRow>
        <TableCell>
          {displayStateIcon(template.templateInfo?.archived, template.deleted)}
        </TableCell>
        <TableCell>
          <a
            href={`/search/doc/${template.templateInfo?.id ?? ""}`}
            className={clsx(classes.removeLinkStyle)}
            data-testid={`template-link-${template.templateInfo?.id}`}
          >
            <Typography color="primary" fontWeight="bold" variant="subtitle1">
              {template.templateInfo?.name}
            </Typography>
          </a>
          <Typography
            variant="body2"
            align="left"
            sx={classes.clickable}
            onClick={() => copyToClipboard(template.templateInfo?.id ?? "")}
            noWrap={true}
          >
            {template.templateInfo?.id}
            <Clipboard
              color={theme.palette.text.primary}
              size={"1rem"}
              className="position-icon"
            />
          </Typography>
        </TableCell>
        <TableCell>{template.templateInfo?.description}</TableCell>
        <TableCell>
          <Link
            to={`/user/${regionCode}/${template.templateInfo?.owner?.id}`}
            className="remove-link-style"
          >
            <Typography color="primary">
              {template.templateInfo?.owner?.name}
            </Typography>
          </Link>
        </TableCell>
        <TableCell>
          <b>Created: </b>
          {displayDate(template.templateInfo?.createdAt)}
        </TableCell>
        <TableCell>
          <b>Modified: </b>
          {displayDate(template.templateInfo?.modifiedAt)}
          <br />
          <b>Last used: </b>
          {displayDate(template.templateInfo?.lastUsed)}
        </TableCell>
        <TableCell>{template.inspectionsCount}</TableCell>
        <TableCell>
          <KebabMenu>
            <MenuItem>
              <Link
                to={`/template/${regionCode}/${template.templateInfo?.id}/access`}
                className="remove-link-style"
                data-testid={`template-access-link-${template.templateInfo?.id}`}
              >
                <Typography>View Template Access Rules</Typography>
              </Link>
            </MenuItem>
          </KebabMenu>
        </TableCell>
      </TableRow>
    );
  };

  const displayPaginationControls = () => {
    if (totalItems === 0 || totalItems === TOTAL_ITEMS_UNINITIALISED)
      return null;
    return (
      <TablePagination
        component="div"
        count={totalItems}
        page={Math.floor(offset / pageSize)}
        onPageChange={() => setOffset((prev) => prev + pageSize)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => {
          const newPageSize = parseInt(e.target.value);
          const newOffset = Math.floor(offset / pageSize) * pageSize;
          setPageSize(newPageSize);
          setOffset(newOffset);
        }}
      />
    );
  };
  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Stack spacing={2} my={2}>
          {!orgID && (
            <Stack direction="row" spacing={2} justifyContent="center">
              {orgID ? null : (
                <TextField
                  id="roleId-input"
                  label="Org ID"
                  value={templateSearchFormState.orgID}
                  onChange={(event) => handleChange(event, "orgID")}
                  variant="outlined"
                  size="small"
                  sx={{ width: 500, fullWidth: "100%" }}
                  inputProps={{ "data-anchor": "org-id-input" }}
                  onKeyUp={(event) => {
                    if (event.key === "Enter") {
                      handleListOrgTemplates();
                    }
                  }}
                />
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleListOrgTemplates}
                data-anchor="template-search-btn"
                data-testid="template-search-btn"
                disabled={!templateSearchFormState.orgID}
              >
                Search
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={clearForm}
                data-anchor="template-search-clear-btn"
                data-testid="template-search-clear-btn"
              >
                Clear
              </Button>
            </Stack>
          )}
          <Stack direction="row" spacing={2} justifyContent="center">
            <TextField
              id="template-owner-input"
              label="Template owner ID"
              variant="outlined"
              size="small"
              value={templateSearchFormState.ownerID}
              onChange={(e) => handleChange(e, "ownerID")}
            />
            <TextField
              id="template-name-input"
              label="Template name"
              variant="outlined"
              size="small"
              sx={{ width: 300, fullWidth: "100%" }}
              value={templateSearchFormState.name}
              onChange={(e) => handleChange(e, "name")}
            />
            <DatePicker
              label="Modified from"
              size="small"
              defaultDateSelection={templateSearchFormState.dateFrom}
              onChange={(date) => handleDateChange(date, "dateFrom")}
              minDate={minDate}
              maxDate={maxDate}
            />
            <DatePicker
              label="Modified until"
              size="small"
              defaultDateSelection={templateSearchFormState.dateTo}
              onChange={(date) => handleDateChange(date, "dateTo")}
              minDate={minDate}
              maxDate={maxDate}
            />
            <FormControl>
              <InputLabel id="template-state-input-label">State</InputLabel>
              <Select
                id="template-state-input"
                labelId="template-state-input-label"
                label="State"
                size="small"
                defaultValue={DocumentState.ACTIVE}
                onChange={(e) => handleChange(e, "state")}
              >
                <MenuItem value={DocumentState.ACTIVE}>Active</MenuItem>
                <MenuItem value={DocumentState.ARCHIVED}>
                  Include archived
                </MenuItem>
                <MenuItem value={DocumentState.DELETED}>
                  Include archived and deleted
                </MenuItem>
              </Select>
            </FormControl>
          </Stack>
          {orgID && (
            <Stack direction="row" spacing={2} justifyContent="center">
              {orgID ? null : (
                <TextField
                  id="roleId-input"
                  label="Org ID"
                  value={templateSearchFormState.orgID}
                  onChange={(event) => handleChange(event, "orgID")}
                  variant="outlined"
                  size="small"
                  sx={{ width: 500, fullWidth: "100%" }}
                  inputProps={{ "data-anchor": "org-id-input" }}
                  onKeyUp={(event) => {
                    if (event.key === "Enter") {
                      handleListOrgTemplates();
                    }
                  }}
                />
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleListOrgTemplates}
                data-anchor="template-search-btn"
                data-testid="template-search-btn"
                disabled={!templateSearchFormState.orgID}
              >
                Search
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={clearForm}
                data-anchor="template-search-clear-btn"
                data-testid="template-search-clear-btn"
              >
                Clear
              </Button>
            </Stack>
          )}
          <Tooltip
            title={
              <p>
                <p>
                  You may need to change the State field to include archived
                  and/or deleted templates in your search.
                </p>
                <p>
                  You may need to change the Modified From field to be earlier.
                  The max is 5 years into the past.
                </p>
                <p>You may have misspelt the template name.</p>
              </p>
            }
          >
            <Typography variant="caption" sx={classes.coloredLinkStyle}>
              Why can&apos;t I find the templates?
            </Typography>
          </Tooltip>
        </Stack>

        <DisplayError errorMessage={errorValidationMessage} />

        <Paper>
          {displayPaginationControls()}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{/* State icon */}</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortParam.field === DocumentSortType.NAME}
                    direction={
                      sortParam.direction === SortDirection.DESC
                        ? "desc"
                        : "asc"
                    }
                    onClick={() =>
                      setSortParam((prev) => {
                        return {
                          field: DocumentSortType.NAME,
                          direction:
                            prev.direction === SortDirection.ASC
                              ? SortDirection.DESC
                              : SortDirection.ASC,
                        };
                      })
                    }
                  >
                    <Tooltip title="Sort by name">
                      <b className={clsx(classes.coloredLinkStyle)}>
                        <u>Name</u>
                      </b>
                    </Tooltip>
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <b>Description</b>
                </TableCell>
                <TableCell>
                  <b>Owner</b>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortParam.field === DocumentSortType.DATE_CREATED}
                    direction={
                      sortParam.direction === SortDirection.DESC
                        ? "desc"
                        : "asc"
                    }
                    onClick={() =>
                      setSortParam((prev) => {
                        return {
                          field: DocumentSortType.DATE_CREATED,
                          direction:
                            prev.direction === SortDirection.ASC
                              ? SortDirection.DESC
                              : SortDirection.ASC,
                        };
                      })
                    }
                  >
                    <Tooltip title="Sort by created time">
                      <b className={clsx(classes.coloredLinkStyle)}>
                        <u>Created</u>
                      </b>
                    </Tooltip>
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortParam.field === DocumentSortType.DATE_MODIFIED}
                    direction={
                      sortParam.direction === SortDirection.DESC
                        ? "desc"
                        : "asc"
                    }
                    onClick={() =>
                      setSortParam((prev) => {
                        return {
                          field: DocumentSortType.DATE_MODIFIED,
                          direction:
                            prev.direction === SortDirection.ASC
                              ? SortDirection.DESC
                              : SortDirection.ASC,
                        };
                      })
                    }
                  >
                    <Tooltip title="Sort by modified time">
                      <b className={clsx(classes.coloredLinkStyle)}>
                        <u>Modified</u>
                      </b>
                    </Tooltip>
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <b>Inspections count</b>
                </TableCell>
                <TableCell>{/* Actions */}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : (
                templates.map((template) => {
                  return displayTemplateRow(template);
                })
              )}
            </TableBody>
          </Table>

          {displayPaginationControls()}
          {!isLoading && totalItems === 0 && !isFirstRender.current && (
            <Typography py={3}>
              <b>No templates found</b>
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default TemplateSearch;
